import React from 'react';
import { ArrowRight } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import type { PageType } from './Header';

type HeroProps = {
  onNavigate: (page: PageType) => void;
};

export const Hero: React.FC<HeroProps> = ({ onNavigate }) => {
  const { isDark } = useTheme();

  return (
    <section className="relative w-full min-h-[90vh] overflow-hidden">
      {/* Video Background */}
      <div className="absolute inset-0 bg-vmil-navy">
        <video 
          autoPlay 
          loop 
          muted 
          playsInline
          preload="auto"
          className="w-full h-full object-cover"
        >
          <source src="/vmil-hero.mp4" type="video/mp4" />
        </video>
        {/* Minimal overlay for text readability */}
        <div className="absolute inset-0 bg-black/10" />
      </div>

      {/* Animated Background Elements - Blue only */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-vmil-blue/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-vmil-blue/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      {/* Grid Pattern Overlay */}
      <div 
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '50px 50px'
        }}
      />

      <div className="container relative z-10 mx-auto px-4 py-16 sm:py-24 md:py-32 lg:py-40">
        <div className="max-w-4xl text-left">
          {/* Main Headline */}
          <div className="space-y-4 sm:space-y-6 mb-8">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-white leading-tight">
              Manufacturing Partner for Global Core Industries
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-white/80 max-w-2xl">
              Built to International Standards | Trusted Since 1979
            </p>
          </div>

          {/* CTA Buttons - Blue only */}
          <div className="flex flex-col sm:flex-row gap-4 justify-start">
            <button
              onClick={() => onNavigate('products')}
              className="group inline-flex h-14 items-center justify-center rounded-xl bg-vmil-blue px-8 text-lg font-bold text-white shadow-lg shadow-vmil-blue/25 transition-all hover:shadow-xl hover:shadow-vmil-blue/30 hover:scale-[1.02]"
            >
              Explore Industries
              <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => onNavigate('infrastructure')}
              className="inline-flex h-14 items-center justify-center rounded-xl border-2 border-white/30 px-8 text-lg font-bold text-white transition-all hover:bg-white/10 hover:border-white/50"
            >
              Explore Capabilities
            </button>
          </div>
        </div>
      </div>

      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path 
            d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" 
            fill={isDark ? '#000000' : '#ffffff'}
          />
        </svg>
      </div>
    </section>
  );
};
